import React, { useEffect, useState, useRef, useContext } from 'react';
import { MenuIcon, XIcon, ChevronDownIcon, LogOutIcon, UserIcon, HomeIcon, BookOpenIcon, CalendarIcon, MessageSquareIcon, GraduationCapIcon, UsersIcon, ClockIcon, InfoIcon, SunIcon, MoonIcon, SearchIcon, SettingsIcon, ShieldIcon, CreditCardIcon, HelpCircleIcon } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { SignInModalContext } from '../App';
import { NotificationCenter } from './NotificationCenter';
import { useTheme } from './providers/ThemeProvider';
export const Navbar = ({
  setIsDeleteAccountModalOpen
}) => {
  const {
    currentUser,
    userData,
    userType,
    logout
  } = useAuth();
  const {
    openTeacherLoginModal
  } = useContext(SignInModalContext);
  const {
    theme,
    setTheme
  } = useTheme();
  const isDark = theme === 'dark';
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDesktopMenuOpen, setIsDesktopMenuOpen] = useState(false);
  const [isAccountDropdownOpen, setIsAccountDropdownOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  // ---- FIXED: Clean userType normalization ----
  const normalizedUserType = userType ? userType.toLowerCase().trim() : null;
  const isStudent = normalizedUserType === 'student';
  const isTeacher = normalizedUserType === 'teacher';
  // ---- FIXED: Single source of truth for dashboard routing ----
  let dashboardPath = '/';
  if (isStudent) {
    dashboardPath = '/student-dashboard';
  } else if (isTeacher) {
    dashboardPath = '/teacher-dashboard';
  }
  // ---- FIXED: Single source of truth for profile routing ----
  let profilePath = '/';
  if (isStudent) {
    profilePath = '/my-profile';
  } else if (isTeacher) {
    profilePath = '/teacher-profile';
  }
  // ---- FIXED: Single source of truth for calendar routing ----
  let calendarPath = '/';
  if (isStudent) {
    calendarPath = '/student-schedule';
  } else if (isTeacher) {
    calendarPath = '/teacher/calendar';
  }
  // ---- FIXED: Single source of truth for messages routing ----
  let messagesPath = '/';
  if (isStudent) {
    messagesPath = '/messages';
  } else if (isTeacher) {
    messagesPath = '/teacher-messages';
  }
  // Toggle theme
  const toggleTheme = () => {
    setTheme(isDark ? 'light' : 'dark');
  };
  const accountDropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  // ---- FIXED: Dedicated Dashboard click handler ----
  const handleDashboardClick = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
    }
    if (!currentUser) {
      // Not authenticated → go home
      navigate('/');
      return;
    }
    if (!dashboardPath || dashboardPath === '/') {
      // Authenticated but no role → safe fallback to student dashboard
      navigate('/student-dashboard');
      return;
    }
    // Navigate to the correct dashboard based on userType
    navigate(dashboardPath);
    window.scrollTo(0, 0);
  };
  // ---- FIXED: Dedicated Profile click handler ----
  const handleProfileClick = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
    }
    if (!currentUser) {
      // Not authenticated → go home
      navigate('/');
      return;
    }
    if (isStudent) {
      navigate(profilePath); // student profile route
      window.scrollTo(0, 0);
      return;
    }
    if (isTeacher) {
      navigate(profilePath); // teacher profile route
      window.scrollTo(0, 0);
      return;
    }
    // Fallback if userType is unknown
    navigate('/');
  };
  // ---- FIXED: Dedicated Calendar click handler ----
  const handleCalendarClick = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
    }
    if (!currentUser) {
      // Not authenticated → go home
      navigate('/');
      return;
    }
    if (isStudent) {
      navigate(calendarPath); // student calendar/schedule route
      window.scrollTo(0, 0);
      return;
    }
    if (isTeacher) {
      navigate(calendarPath); // teacher calendar route
      window.scrollTo(0, 0);
      return;
    }
    // Fallback if userType is unknown
    navigate('/');
  };
  // ---- FIXED: Dedicated Messages click handler ----
  const handleMessagesClick = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
    }
    if (!currentUser) {
      // Not authenticated → go home
      navigate('/');
      return;
    }
    if (isStudent) {
      navigate(messagesPath); // student messages route
      window.scrollTo(0, 0);
      return;
    }
    if (isTeacher) {
      navigate(messagesPath); // teacher messages route
      window.scrollTo(0, 0);
      return;
    }
    // Fallback if userType is unknown
    navigate('/');
  };
  // Check if user is logged in based on Firebase auth
  useEffect(() => {
    const checkUserLoggedIn = () => {
      // STEP 1 FIX: Use Firebase currentUser as the SOLE source of truth
      // Removed the isOnDashboardPages() fallback that caused desync on cross-tab logout
      if (currentUser) {
        setIsLoggedIn(true);
      } else {
        setIsLoggedIn(false);
      }
      // Set loading to false after checking
      setIsLoading(false);
    };
    checkUserLoggedIn();
  }, [currentUser, location.pathname]);
  // Check if current location is a dashboard/logged-in page
  const isOnDashboardPages = () => {
    return location.pathname === '/student-dashboard' || location.pathname === '/messages' || location.pathname === '/student-schedule' || location.pathname === '/online-courses' || location.pathname === '/find-teachers' || location.pathname === '/teacher-dashboard' || location.pathname === '/teacher-profile' || location.pathname === '/my-students' || location.pathname === '/my-availability' || location.pathname.startsWith('/teacher/') || isOnMyAccountPage();
  };
  // Check if current location is a My Account page
  const isOnMyAccountPage = () => {
    return location.pathname === '/my-info' || location.pathname === '/settings' || location.pathname === '/account-security' || location.pathname === '/billing-payouts' || location.pathname === '/help-support';
  };
  // Check if current location is a teacher dashboard page
  const isOnTeacherDashboardPage = () => {
    return location.pathname === '/teacher-dashboard' || location.pathname === '/teacher-profile' || location.pathname === '/teacher-classes' || location.pathname === '/reports' || location.pathname === '/my-availability' || location.pathname === '/my-students' || location.pathname === '/messages' || location.pathname === '/teacher-messages' || location.pathname === '/assigned-tasks' || location.pathname === '/teacher-sheet-music' || location.pathname === '/teacher/calendar2' || location.pathname.startsWith('/student-profile/') || isOnMyAccountPage();
  };
  // Check if current location is a student page
  const isOnStudentPage = () => {
    return location.pathname === '/student-dashboard' || location.pathname === '/find-teachers' || location.pathname === '/student-schedule' || location.pathname === '/messages' || location.pathname === '/online-courses' || location.pathname === '/sheet-music' || location.pathname === '/student-settings' || isOnMyAccountPage();
  };
  useEffect(() => {
    let ticking = false;
    let lastScrolled = false;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const scrolled = window.scrollY > 10;
        if (scrolled !== lastScrolled) {
          lastScrolled = scrolled;
          setIsScrolled(scrolled);
        }
        ticking = false;
      });
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Close dropdown when clicking outside - only add this effect when dropdown is open
  useEffect(() => {
    if (!isAccountDropdownOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (accountDropdownRef.current && !accountDropdownRef.current.contains(event.target as Node)) {
        setIsAccountDropdownOpen(false);
      }
    };
    // Add event listener with a small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isAccountDropdownOpen]);
  const handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Always prevent default navigation
    e.preventDefault();
    if (isLoggedIn) {
      // If logged in, navigate to dashboard (not landing page)
      if (dashboardPath && dashboardPath !== '/') {
        navigate(dashboardPath);
      } else {
        // Fallback if dashboardPath is not set
        navigate('/teacher-dashboard');
      }
      // Ensure we scroll to top
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } else if (location.pathname === '/') {
      // If we're already on the homepage and not logged in, just scroll to top
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } else {
      // If on another page and not logged in, navigate to home
      // Store a flag in localStorage to indicate we should scroll to top after navigation
      localStorage.setItem('scrollToTop', 'true');
      navigate('/');
    }
  };
  const handleFindTeacherClick = () => {
    // Ensure we scroll to top when navigating to student signup
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 100);
  };
  const handleNavLinkClick = (sectionId: string) => {
    // If logged in, don't navigate to homepage sections
    if (isLoggedIn) {
      return;
    }
    if (location.pathname === '/') {
      // If already on homepage, just scroll to the section
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth'
        });
      }
    } else {
      // If on another page, navigate to homepage first, then scroll to section
      // We'll use localStorage to remember which section to scroll to after navigation
      localStorage.setItem('scrollToSection', sectionId);
    }
    // Close the desktop menu if it's open
    setIsDesktopMenuOpen(false);
  };
  // New function to handle scroll to top for already active pages
  const handleSamePageNavigation = (path: string, e: React.MouseEvent) => {
    if (location.pathname === path) {
      e.preventDefault();
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };
  // Open teacher login modal directly (skip role selection for teacher-only beta)
  const handleOpenSignInModal = () => {
    openTeacherLoginModal();
    setIsMenuOpen(false); // Close mobile menu if open
    setIsDesktopMenuOpen(false); // Close desktop menu if open
  };
  // Check if we need to scroll to a section after navigation
  useEffect(() => {
    if (location.pathname === '/') {
      const sectionId = localStorage.getItem('scrollToSection');
      if (sectionId) {
        // Small delay to ensure the page has loaded
        setTimeout(() => {
          const element = document.getElementById(sectionId);
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth'
            });
          }
          localStorage.removeItem('scrollToSection');
        }, 100);
      }
    }
  }, [location.pathname]);
  // Check if we need to scroll to top after navigation
  useEffect(() => {
    if (location.pathname === '/' && localStorage.getItem('scrollToTop') === 'true') {
      // Small delay to ensure the page has loaded
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: 'auto'
        });
        localStorage.removeItem('scrollToTop');
      }, 100);
    }
  }, [location.pathname]);
  const handleLogout = async () => {
    try {
      // Use Firebase logout
      await logout();
      // Redirect to home page
      navigate('/');
      // Close the dropdown
      setIsAccountDropdownOpen(false);
      // Update login state
      setIsLoggedIn(false);
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };
  // Safe navigation function that maintains authentication state
  const handleAuthenticatedNavigation = (path: string) => {
    console.log('[MOBILE MENU] handleAuthenticatedNavigation called:', path);
    // Conditional navigation based on user type for specific paths
    if (path === '/messages' && isTeacher) {
      navigate('/teacher-messages');
    } else {
      navigate(path);
    }
    // Force immediate scroll to top
    window.scrollTo(0, 0);
    // Close menu AFTER navigation using requestAnimationFrame
    requestAnimationFrame(() => {
      console.log('[MOBILE MENU] Closing menu after navigation');
      setIsMenuOpen(false);
      setIsAccountDropdownOpen(false);
    });
  };
  // Helper for menu actions: execute action first, then close menu
  const handleMenuAction = (action: () => void, actionName: string = 'action') => {
    console.log('[MOBILE MENU] handleMenuAction called:', actionName);
    // Execute the action first
    action();
    // Close menu after action completes using requestAnimationFrame
    requestAnimationFrame(() => {
      console.log('[MOBILE MENU] Closing menu after', actionName);
      setIsMenuOpen(false);
    });
  };
  // If still loading, render a simplified navbar to prevent errors
  if (isLoading) {
    return <nav className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-navy-900/95 backdrop-blur-sm shadow-lg' : 'bg-transparent'}`} data-id="element-2595">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2596">
          <div className="flex items-center justify-between h-16" data-id="element-2597">
            <div className="flex items-center pl-6 md:pl-12" data-id="element-2598">
              <Link to="/" className="text-2xl font-serif font-bold text-white flex items-center" data-id="element-2599">
                <span data-id="element-2600">MaestroConnect</span>
              </Link>
            </div>
          </div>
        </div>
      </nav>;
  }
  return <nav className={`fixed top-0 left-0 right-0 z-50 ${location.pathname === '/' && !isLoggedIn ? 'bg-navy-800/90 backdrop-blur-md' : 'bg-navy-800/90 backdrop-blur-md'} shadow-lg`} data-id="element-2601">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2602">
        <div className="flex justify-between items-center h-20" data-id="element-2603">
          {/* Logo - Now aligned with main content */}
          <a href="/" className="flex items-center" onClick={handleLogoClick} data-id="element-2604">
            <div className="text-xl md:text-2xl font-serif font-bold text-white" data-id="element-2605">
              <span className="text-deep-pink" data-id="element-2606">Maestro</span>Connect
            </div>
          </a>

          <div className="md:hidden flex items-center gap-3" data-id="element-2607">
            {/* Notification bell for mobile - only show when logged in */}
            {isLoggedIn && <NotificationCenter darkMode={isDark} data-id="element-2608" />}

            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="inline-flex items-center justify-center p-3 rounded-md text-gray-400 hover:text-white hover:bg-navy-800 focus:outline-none" aria-label={isMenuOpen ? 'Close menu' : 'Open menu'} style={{
            minWidth: '48px',
            minHeight: '48px'
          }} data-id="element-2609">
              {isMenuOpen ? <XIcon className="block h-6 w-6" aria-hidden="true" data-id="element-2610" /> : <MenuIcon className="block h-6 w-6" aria-hidden="true" data-id="element-2611" />}
            </button>
          </div>

          {/* Desktop Navigation Links */}
          <div className="hidden md:flex items-center space-x-8 justify-center pr-12" data-id="element-2612">
            {isLoggedIn ?
          // Dashboard navigation for logged-in users
          <>
                {isStudent ?
            // Student Navigation - Simplified for all internal pages
            isOnStudentPage() ?
            // Simplified navigation for all student internal pages
            <>
                      <button onClick={handleDashboardClick} className={`text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors flex items-center ${location.pathname === dashboardPath ? 'text-deep-pink' : ''}`} data-id="element-2613">
                        <HomeIcon className="h-4 w-4 mr-1" data-id="element-2614" />
                        Dashboard
                      </button>
                      <Link to="/student-schedule" className={`text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors flex items-center ${location.pathname === '/student-schedule' ? 'text-deep-pink' : ''}`} onClick={e => handleSamePageNavigation('/student-schedule', e)} data-id="element-2615">
                        <CalendarIcon className="h-4 w-4 mr-1" data-id="element-2616" />
                        My Schedule
                      </Link>
                      <Link to="/find-teachers" className={`text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors flex items-center ${location.pathname === '/find-teachers' ? 'text-deep-pink' : ''}`} onClick={e => handleSamePageNavigation('/find-teachers', e)} data-id="element-2617">
                        <SearchIcon className="h-4 w-4 mr-1" data-id="element-2618" />
                        Find Teachers
                      </Link>
                      <Link to="/messages" className={`text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors flex items-center ${location.pathname === '/messages' ? 'text-deep-pink' : ''}`} onClick={e => {
                // Force immediate scroll to top when navigating to messages
                // Use setTimeout to ensure this happens after navigation
                setTimeout(() => {
                  window.scrollTo({
                    top: 0,
                    behavior: 'auto'
                  });
                }, 0);
              }} data-id="element-2619">
                        <MessageSquareIcon className="h-4 w-4 mr-1" data-id="element-2620" />
                        Messages
                      </Link>
                      {/* Add NotificationCenter before dark/light mode toggle */}
                      <div className="px-2 py-1" data-id="element-2621">
                        <NotificationCenter darkMode={isDark} data-id="element-2622" />
                      </div>
                      {/* Dark/Light Mode Toggle for student pages */}
                      {isStudent && isOnStudentPage() && <button onClick={toggleTheme} className="text-white hover:text-gray-300 px-2 py-1 transition-colors flex items-center" aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'} data-id="element-2623">
                          {isDark ? <MoonIcon className="h-5 w-5" data-id="element-2624" /> : <SunIcon className="h-5 w-5" data-id="element-2625" />}
                        </button>}
                    </> : location.pathname === '/' ?
            // Simplified navigation for student on landing page
            <>
                      <button onClick={handleDashboardClick} className="text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors flex items-center" data-id="element-2626">
                        <HomeIcon className="h-4 w-4 mr-1" data-id="element-2627" />
                        Dashboard
                      </button>
                      <Link to="/student-schedule" className="text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors flex items-center" data-id="element-2628">
                        <CalendarIcon className="h-4 w-4 mr-1" data-id="element-2629" />
                        My Schedule
                      </Link>
                      <Link to="/find-teachers" className={`text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors flex items-center ${location.pathname === '/find-teachers' ? 'text-deep-pink' : ''}`} data-id="element-2630">
                        <SearchIcon className="h-4 w-4 mr-1" data-id="element-2631" />
                        Find Teachers
                      </Link>
                      <Link to="/messages" className="text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors flex items-center" data-id="element-2632">
                        <MessageSquareIcon className="h-4 w-4 mr-1" data-id="element-2633" />
                        Messages
                      </Link>
                      {/* Add NotificationCenter before dark/light mode toggle */}
                      <div className="px-2 py-1" data-id="element-2634">
                        <NotificationCenter darkMode={isDark} data-id="element-2635" />
                      </div>
                      {/* Add dark/light mode toggle for My Account pages */}
                      {isOnMyAccountPage() && <button onClick={toggleTheme} className="text-white hover:text-gray-300 px-2 py-1 transition-colors flex items-center" aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'} data-id="element-2636">
                          {isDark ? <MoonIcon className="h-5 w-5" data-id="element-2637" /> : <SunIcon className="h-5 w-5" data-id="element-2638" />}
                        </button>}
                    </> :
            // Regular navigation for other pages
            <>
                      <button onClick={handleDashboardClick} className={`text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors flex items-center ${location.pathname === dashboardPath ? 'text-deep-pink' : ''}`} data-id="element-2639">
                        <HomeIcon className="h-4 w-4 mr-1" data-id="element-2640" />
                        Dashboard
                      </button>
                      <Link to="/find-teachers" className={`text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors flex items-center ${location.pathname === '/find-teachers' ? 'text-deep-pink' : ''}`} data-id="element-2641">
                        <SearchIcon className="h-4 w-4 mr-1" data-id="element-2642" />
                        Find Teachers
                      </Link>
                      <Link to="/student-schedule" className={`text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors flex items-center ${location.pathname === '/student-schedule' ? 'text-deep-pink' : ''}`} onClick={e => handleSamePageNavigation('/student-schedule', e)} data-id="element-2643">
                        <CalendarIcon className="h-4 w-4 mr-1" data-id="element-2644" />
                        My Schedule
                      </Link>
                      <Link to="/online-courses" className={`text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors flex items-center ${location.pathname === '/online-courses' ? 'text-deep-pink' : ''}`} data-id="element-2645">
                        <GraduationCapIcon className="h-4 w-4 mr-1" data-id="element-2646" />
                        Online Courses
                      </Link>
                      <Link to="/messages" className={`text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors flex items-center ${location.pathname === '/messages' ? 'text-deep-pink' : ''}`} onClick={e => {
                // Force immediate scroll to top when navigating to messages
                // Use setTimeout to ensure this happens after navigation
                setTimeout(() => {
                  window.scrollTo({
                    top: 0,
                    behavior: 'auto'
                  });
                }, 0);
              }} data-id="element-2647">
                        <MessageSquareIcon className="h-4 w-4 mr-1" data-id="element-2648" />
                        Messages
                      </Link>
                      {/* Add NotificationCenter before dark/light mode toggle */}
                      <div className="px-2 py-1" data-id="element-2649">
                        <NotificationCenter darkMode={isDark} data-id="element-2650" />
                      </div>
                      {/* Dark/Light Mode Toggle for student pages and My Account pages */}
                      {isStudent && isOnStudentPage() || isOnMyAccountPage() && <button onClick={toggleTheme} className="text-white hover:text-gray-300 px-2 py-1 transition-colors flex items-center" aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'} data-id="element-2651">
                            {isDark ? <MoonIcon className="h-5 w-5" data-id="element-2652" /> : <SunIcon className="h-5 w-5" data-id="element-2653" />}
                          </button>}
                    </> :
            // Teacher Navigation
            <>
                    <button onClick={handleDashboardClick} className={`text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors flex items-center ${location.pathname === dashboardPath ? 'text-deep-pink' : ''}`} data-id="element-2654">
                      <HomeIcon className="h-4 w-4 mr-1" data-id="element-2655" />
                      Dashboard
                    </button>
                    <button onClick={handleProfileClick} className={`text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors flex items-center ${location.pathname === profilePath ? 'text-deep-pink' : ''}`} data-id="element-2656">
                      <UserIcon className="h-4 w-4 mr-1" data-id="element-2657" />
                      Teacher Page
                    </button>
                    <button onClick={handleCalendarClick} className={`text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors flex items-center ${location.pathname === calendarPath ? 'text-deep-pink' : ''}`} data-id="element-2658">
                      <CalendarIcon className="h-4 w-4 mr-1" data-id="element-2659" />
                      Calendar
                    </button>
                    {/* TEMPORARILY HIDDEN: Messages feature not ready for teachers */}
                    {/* <button
                     onClick={handleMessagesClick}
                     className={`text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors flex items-center ${location.pathname === messagesPath ? 'text-deep-pink' : ''}`}
                     >
                     <MessageSquareIcon className="h-4 w-4 mr-1" />
                     Messages
                     </button> */}
                    {/* Add NotificationCenter before dark/light mode toggle */}
                    <div className="px-2 py-1" data-id="element-2660">
                      <NotificationCenter darkMode={isDark} data-id="element-2661" />
                    </div>
                    {/* Dark/Light Mode Toggle — always show on teacher pages and My Account pages */}
                    {(isTeacher || isOnTeacherDashboardPage() || isOnMyAccountPage()) && <button onClick={toggleTheme} className="text-white hover:text-gray-300 px-2 py-1 transition-colors flex items-center" aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'} data-id="element-2662">
                        {isDark ? <MoonIcon className="h-5 w-5" data-id="element-2663" /> : <SunIcon className="h-5 w-5" data-id="element-2664" />}
                      </button>}
                  </>}

                {/* Account Dropdown for both user types */}
                <div className="relative account-dropdown" ref={accountDropdownRef} data-id="element-2665">
                  <button onClick={() => setIsAccountDropdownOpen(!isAccountDropdownOpen)} className="bg-[#3B82F6] border-2 border-[#3B82F6] hover:bg-[#2563EB] text-white text-sm py-1 px-3 rounded-lg transition-colors flex items-center" data-id="element-2666">
                    My Account
                    <ChevronDownIcon className="h-4 w-4 ml-1" data-id="element-2667" />
                  </button>
                  {isAccountDropdownOpen && <div className={`absolute right-0 mt-2 w-56 ${isDark ? 'bg-navy-800' : 'bg-white'} rounded-lg shadow-lg py-1 z-50`} data-id="element-2668">
                      {/* PHASE 1 FIX: Role-aware dropdown */}
                      {isStudent ?
                // STUDENT MENU: Only My Profile and Logout
                <>
                          <button onClick={() => handleAuthenticatedNavigation('/my-profile')} className={`block w-full text-left px-4 py-2 ${location.pathname === '/my-profile' ? `border-l-2 border-deep-pink font-semibold` : ''} ${isDark ? 'text-white hover:bg-navy-900' : 'text-gray-800 hover:bg-gray-100'} flex items-center`} data-id="element-2669">
                            <UserIcon className="h-4 w-4 mr-2" data-id="element-2670" />
                            My Profile
                          </button>
                          <div className={`border-t ${isDark ? 'border-white/10' : 'border-gray-200'} my-1`} data-id="element-2671"></div>
                          <button onClick={handleLogout} className={`block w-full text-left px-4 py-2 ${isDark ? 'text-white hover:bg-navy-900' : 'text-gray-800 hover:bg-gray-100'} flex items-center`} data-id="element-2672">
                            <LogOutIcon className="h-4 w-4 mr-2" data-id="element-2673" />
                            Logout
                          </button>
                        </> : isTeacher ?
                // TEACHER MENU: Full account management
                <>
                          <button onClick={() => handleAuthenticatedNavigation('/my-info')} className={`block w-full text-left px-4 py-2 ${location.pathname === '/my-info' ? `border-l-2 border-deep-pink font-semibold` : ''} ${isDark ? 'text-white hover:bg-navy-900' : 'text-gray-800 hover:bg-gray-100'} flex items-center`} data-id="element-2674">
                            <InfoIcon className="h-4 w-4 mr-2" data-id="element-2675" />
                            My Info
                          </button>
                          <button onClick={() => handleAuthenticatedNavigation('/settings')} className={`block w-full text-left px-4 py-2 ${location.pathname === '/settings' ? `border-l-2 border-deep-pink font-semibold` : ''} ${isDark ? 'text-white hover:bg-navy-900' : 'text-gray-800 hover:bg-gray-100'} flex items-center`} data-id="element-2676">
                            <SettingsIcon className="h-4 w-4 mr-2" data-id="element-2677" />
                            Settings
                          </button>
                          <button onClick={() => handleAuthenticatedNavigation('/account-security')} className={`block w-full text-left px-4 py-2 ${location.pathname === '/account-security' ? `border-l-2 border-deep-pink font-semibold` : ''} ${isDark ? 'text-white hover:bg-navy-900' : 'text-gray-800 hover:bg-gray-100'} flex items-center`} data-id="element-2678">
                            <ShieldIcon className="h-4 w-4 mr-2" data-id="element-2679" />
                            Account Security
                          </button>
                          <button onClick={() => handleAuthenticatedNavigation('/billing-payouts')} className={`block w-full text-left px-4 py-2 ${location.pathname === '/billing-payouts' ? `border-l-2 border-deep-pink font-semibold` : ''} ${isDark ? 'text-white hover:bg-navy-900' : 'text-gray-800 hover:bg-gray-100'} flex items-center`} data-id="element-2680">
                            <CreditCardIcon className="h-4 w-4 mr-2" data-id="element-2681" />
                            Billing
                          </button>
                          <button onClick={() => handleAuthenticatedNavigation('/help-support')} className={`block w-full text-left px-4 py-2 ${location.pathname === '/help-support' ? `border-l-2 border-deep-pink font-semibold` : ''} ${isDark ? 'text-white hover:bg-navy-900' : 'text-gray-800 hover:bg-gray-100'} flex items-center`} data-id="element-2682">
                            <HelpCircleIcon className="h-4 w-4 mr-2" data-id="element-2683" />
                            Help & Support
                          </button>
                          <div className={`border-t ${isDark ? 'border-white/10' : 'border-gray-200'} my-1`} data-id="element-2684"></div>
                          <button onClick={handleLogout} className={`block w-full text-left px-4 py-2 ${isDark ? 'text-white hover:bg-navy-900' : 'text-gray-800 hover:bg-gray-100'} flex items-center`} data-id="element-2685">
                            <LogOutIcon className="h-4 w-4 mr-2" data-id="element-2686" />
                            Logout
                          </button>
                        </> :
                // STEP 2 FIX: Fallback when userType is null but user appears logged in
                // Ensures logout is always accessible even in desync edge cases
                <>
                          <button onClick={handleLogout} className={`block w-full text-left px-4 py-2 ${isDark ? 'text-white hover:bg-navy-900' : 'text-gray-800 hover:bg-gray-100'} flex items-center`} data-id="element-2687">
                            <LogOutIcon className="h-4 w-4 mr-2" data-id="element-2688" />
                            Logout
                          </button>
                        </>}
                    </div>}
                </div>
              </> : location.pathname === '/' ?
          // Public site navigation - homepage
          <>
                <Link to="/how-it-works" className="text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors" data-id="element-2689">
                  How It Works
                </Link>
                <Link to="/benefits" className="text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors" data-id="element-2690">
                  Benefits
                </Link>
                <Link to="/pricing" className="text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors" data-id="element-2691">
                  Pricing
                </Link>
                {/* TEMPORARILY HIDDEN: Courses page shows "Coming soon" placeholder */}
                {/* <Link
                 to="/courses"
                 className="text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors"
                 >
                 Courses
                 </Link> */}
                <Link to="/tutorial" className="text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors" data-id="element-2692">
                  Tutorial
                </Link>
                <Link to="/contact" className="text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors" data-id="element-2693">
                  Contact
                </Link>

                <Link to="/login" className="text-deep-pink hover:text-burgundy px-2 py-1 text-sm font-medium transition-colors" data-id="element-2694">
                  Sign In
                </Link>
              </> :
          // Public site navigation - other pages
          <>
                <Link to="/how-it-works" className="text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors" data-id="element-2695">
                  How It Works
                </Link>
                <Link to="/benefits" className="text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors" data-id="element-2696">
                  Benefits
                </Link>
                <Link to="/pricing" className="text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors" data-id="element-2697">
                  Pricing
                </Link>
                {/* TEMPORARILY HIDDEN: Courses page shows "Coming soon" placeholder */}
                {/* <Link
                 to="/courses"
                 className="text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors"
                 >
                 Courses
                 </Link> */}
                <Link to="/contact" className="text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors" data-id="element-2698">
                  Contact
                </Link>
                <Link to="/login" className="text-deep-pink hover:text-burgundy px-2 py-1 text-sm font-medium transition-colors" data-id="element-2699">
                  Sign In
                </Link>
              </>}
          </div>
        </div>
      </div>

      {/* Mobile menu - improved styling and touch targets */}
      {isMenuOpen && <div className={`md:hidden ${isDark ? 'bg-navy-800' : 'bg-white'} relative z-20`} ref={mobileMenuRef} onPointerDown={e => {
      // Stop propagation to prevent overlay from catching events
      e.stopPropagation();
      console.log('[MOBILE MENU] Menu container pointer down - propagation stopped');
    }} data-id="element-2700">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 shadow-inner" data-id="element-2701">
            {isLoggedIn ?
        // Dashboard mobile navigation for logged-in users
        <>
                {isStudent ?
          // Student mobile navigation - improved touch targets
          <>
                    <button onClick={() => {
              handleDashboardClick();
              setIsMenuOpen(false);
            }} className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium ${location.pathname === dashboardPath ? isDark ? 'bg-navy-900 text-white' : 'bg-gray-100 text-gray-900' : isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-2702">
                      <div className="flex items-center" data-id="element-2703">
                        <HomeIcon className="h-5 w-5 mr-3" data-id="element-2704" />
                        Dashboard
                      </div>
                    </button>
                    <button onClick={() => handleAuthenticatedNavigation('/find-teachers')} className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium ${location.pathname === '/find-teachers' ? isDark ? 'bg-navy-900 text-white' : 'bg-gray-100 text-gray-900' : isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-2705">
                      <div className="flex items-center" data-id="element-2706">
                        <SearchIcon className="h-5 w-5 mr-3" data-id="element-2707" />
                        Find Teachers
                      </div>
                    </button>
                    <button onClick={() => handleAuthenticatedNavigation('/student-schedule')} className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium ${location.pathname === '/student-schedule' ? isDark ? 'bg-navy-900 text-white' : 'bg-gray-100 text-gray-900' : isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-2708">
                      <div className="flex items-center" data-id="element-2709">
                        <CalendarIcon className="h-5 w-5 mr-3" data-id="element-2710" />
                        My Schedule
                      </div>
                    </button>
                    <button onClick={() => handleAuthenticatedNavigation('/online-courses')} className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium ${location.pathname === '/online-courses' ? isDark ? 'bg-navy-900 text-white' : 'bg-gray-100 text-gray-900' : isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-2711">
                      <div className="flex items-center" data-id="element-2712">
                        <GraduationCapIcon className="h-5 w-5 mr-3" data-id="element-2713" />
                        Online Courses
                      </div>
                    </button>
                    <button onClick={() => handleAuthenticatedNavigation('/messages')} className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium ${location.pathname === '/messages' ? isDark ? 'bg-navy-900 text-white' : 'bg-gray-100 text-gray-900' : isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-2714">
                      <div className="flex items-center" data-id="element-2715">
                        <MessageSquareIcon className="h-5 w-5 mr-3" data-id="element-2716" />
                        Messages
                      </div>
                    </button>
                    {/* Dark/Light Mode Toggle for mobile - Add for student pages and My Account pages */}
                    {isStudent && isOnStudentPage() || isOnMyAccountPage() && <button onClick={toggleTheme} className={`flex items-center px-4 py-3 rounded-md text-base font-medium w-full ${isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-2717">
                          {isDark ? <>
                              <MoonIcon className="h-5 w-5 mr-3" data-id="element-2718" />
                              Dark Mode
                            </> : <>
                              <SunIcon className="h-5 w-5 mr-3" data-id="element-2719" />
                              Light Mode
                            </>}
                        </button>}
                  </> :
          // Teacher mobile navigation - Reordered links and improved touch targets
          <>
                    <button onClick={() => {
              console.log('[MOBILE MENU] Dashboard button clicked');
              handleMenuAction(handleDashboardClick, 'Dashboard navigation');
            }} className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium ${location.pathname === dashboardPath ? isDark ? 'bg-navy-900 text-white' : 'bg-gray-100 text-gray-900' : isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-2720">
                      <div className="flex items-center" data-id="element-2721">
                        <HomeIcon className="h-5 w-5 mr-3" data-id="element-2722" />
                        Dashboard
                      </div>
                    </button>
                    <button onClick={() => {
              console.log('[MOBILE MENU] My Profile button clicked');
              handleMenuAction(handleProfileClick, 'Profile navigation');
            }} className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium ${location.pathname === profilePath ? isDark ? 'bg-navy-900 text-white' : 'bg-gray-100 text-gray-900' : isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-2723">
                      <div className="flex items-center" data-id="element-2724">
                        <UserIcon className="h-5 w-5 mr-3" data-id="element-2725" />
                        Teacher Page
                      </div>
                    </button>
                    <button onClick={() => {
              console.log('[MOBILE MENU] Calendar button clicked');
              handleMenuAction(handleCalendarClick, 'Calendar navigation');
            }} className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium ${location.pathname === calendarPath ? isDark ? 'bg-navy-900 text-white' : 'bg-gray-100 text-gray-900' : isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-2726">
                      <div className="flex items-center" data-id="element-2727">
                        <CalendarIcon className="h-5 w-5 mr-3" data-id="element-2728" />
                        Calendar
                      </div>
                    </button>
                    {/* TEMPORARILY HIDDEN: Messages feature not ready for teachers */}
                    {/* <button
                      onClick={() => {
                        console.log('[MOBILE MENU] Messages button clicked')
                        handleAuthenticatedNavigation('/messages')
                      }}
                      className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium ${location.pathname === '/messages' || location.pathname === '/teacher-messages' ? (isDark ? 'bg-navy-900 text-white' : 'bg-gray-100 text-gray-900') : isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`}
                     >
                      <div className="flex items-center">
                        <MessageSquareIcon className="h-5 w-5 mr-3" />
                        Messages
                      </div>
                     </button> */}
                    {/* Dark/Light Mode Toggle for mobile — always show on teacher pages and My Account pages */}
                    {(isTeacher || isOnTeacherDashboardPage() || isOnMyAccountPage()) && <button onClick={() => {
              console.log('[MOBILE MENU] Theme toggle clicked');
              handleMenuAction(toggleTheme, 'Theme toggle');
            }} className={`flex items-center px-4 py-3 rounded-md text-base font-medium w-full ${isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-2729">
                        {isDark ? <>
                            <MoonIcon className="h-5 w-5 mr-3" data-id="element-2730" />
                            Dark Mode
                          </> : <>
                            <SunIcon className="h-5 w-5 mr-3" data-id="element-2731" />
                            Light Mode
                          </>}
                      </button>}
                  </>}

                {/* Account dropdown items for mobile - PHASE 1 FIX: Role-aware */}
                <div className={`border-t ${isDark ? 'border-white/10' : 'border-gray-200'} my-2 pt-2`} data-id="element-2732">
                  {isStudent ?
            // STUDENT MOBILE MENU: Only My Profile
            <button onClick={() => {
              console.log('[MOBILE MENU] My Profile (account) clicked');
              handleAuthenticatedNavigation('/my-profile');
            }} className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium ${location.pathname === '/my-profile' ? isDark ? 'bg-navy-900 border-l-2 border-deep-pink font-semibold text-white' : 'bg-gray-100 border-l-2 border-deep-pink font-semibold text-gray-900' : isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-2733">
                      <div className="flex items-center" data-id="element-2734">
                        <UserIcon className="h-5 w-5 mr-3" data-id="element-2735" />
                        My Profile
                      </div>
                    </button> : isTeacher ?
            // TEACHER MOBILE MENU: Full account management
            <>
                      <button onClick={() => {
                console.log('[MOBILE MENU] My Info clicked');
                handleAuthenticatedNavigation('/my-info');
              }} className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium ${location.pathname === '/my-info' ? isDark ? 'bg-navy-900 border-l-2 border-deep-pink font-semibold text-white' : 'bg-gray-100 border-l-2 border-deep-pink font-semibold text-gray-900' : isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-2736">
                        <div className="flex items-center" data-id="element-2737">
                          <InfoIcon className="h-5 w-5 mr-3" data-id="element-2738" />
                          My Info
                        </div>
                      </button>
                      <button onClick={() => {
                console.log('[MOBILE MENU] Settings clicked');
                handleAuthenticatedNavigation('/settings');
              }} className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium ${location.pathname === '/settings' ? isDark ? 'bg-navy-900 border-l-2 border-deep-pink font-semibold text-white' : 'bg-gray-100 border-l-2 border-deep-pink font-semibold text-gray-900' : isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-2739">
                        <div className="flex items-center" data-id="element-2740">
                          <SettingsIcon className="h-5 w-5 mr-3" data-id="element-2741" />
                          Settings
                        </div>
                      </button>
                      <button onClick={() => {
                console.log('[MOBILE MENU] Account Security clicked');
                handleAuthenticatedNavigation('/account-security');
              }} className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium ${location.pathname === '/account-security' ? isDark ? 'bg-navy-900 border-l-2 border-deep-pink font-semibold text-white' : 'bg-gray-100 border-l-2 border-deep-pink font-semibold text-gray-900' : isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-2742">
                        <div className="flex items-center" data-id="element-2743">
                          <ShieldIcon className="h-5 w-5 mr-3" data-id="element-2744" />
                          Account Security
                        </div>
                      </button>
                      <button onClick={() => {
                console.log('[MOBILE MENU] Billing clicked');
                handleAuthenticatedNavigation('/billing-payouts');
              }} className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium ${location.pathname === '/billing-payouts' ? isDark ? 'bg-navy-900 border-l-2 border-deep-pink font-semibold text-white' : 'bg-gray-100 border-l-2 border-deep-pink font-semibold text-gray-900' : isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-2745">
                        <div className="flex items-center" data-id="element-2746">
                          <CreditCardIcon className="h-5 w-5 mr-3" data-id="element-2747" />
                          Billing
                        </div>
                      </button>
                      <button onClick={() => {
                console.log('[MOBILE MENU] Help & Support clicked');
                handleAuthenticatedNavigation('/help-support');
              }} className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium ${location.pathname === '/help-support' ? isDark ? 'bg-navy-900 border-l-2 border-deep-pink font-semibold text-white' : 'bg-gray-100 border-l-2 border-deep-pink font-semibold text-gray-900' : isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-2748">
                        <div className="flex items-center" data-id="element-2749">
                          <HelpCircleIcon className="h-5 w-5 mr-3" data-id="element-2750" />
                          Help & Support
                        </div>
                      </button>
                    </> : null}
                </div>

                {/* Logout button for both user types — STEP 2 FIX: Always visible regardless of userType */}
                <button onClick={handleLogout} className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium ${isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'} flex items-center`} data-id="element-2751">
                  <LogOutIcon className="h-5 w-5 mr-3" data-id="element-2752" />
                  Logout
                </button>
              </> :
        // Public site navigation - mobile
        <>
                <Link to="/how-it-works" className={`block px-4 py-3 rounded-md text-base font-medium ${isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} onClick={() => setIsMenuOpen(false)} data-id="element-2753">
                  How It Works
                </Link>
                <Link to="/benefits" className={`block px-4 py-3 rounded-md text-base font-medium ${isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} onClick={() => setIsMenuOpen(false)} data-id="element-2754">
                  Benefits
                </Link>
                <Link to="/pricing" className={`block px-4 py-3 rounded-md text-base font-medium ${isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} onClick={() => setIsMenuOpen(false)} data-id="element-2755">
                  Pricing
                </Link>
                <Link to="/tutorial" className={`block px-4 py-3 rounded-md text-base font-medium ${isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} onClick={() => setIsMenuOpen(false)} data-id="element-2756">
                  Tutorial
                </Link>
                {/* TEMPORARILY HIDDEN: Courses page shows "Coming soon" placeholder */}
                {/* <Link
                  to="/courses"
                  className={`block px-4 py-3 rounded-md text-base font-medium ${isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`}
                  onClick={() => setIsMenuOpen(false)}
                 >
                  Courses
                 </Link> */}
                <Link to="/contact" className={`block px-4 py-3 rounded-md text-base font-medium ${isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} onClick={() => setIsMenuOpen(false)} data-id="element-2757">
                  Contact
                </Link>
              </>}
          </div>
        </div>}

      {/* Overlay for mobile when menu is open */}
      {isMenuOpen && <div className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-10" onClick={() => {
      console.log('[MOBILE MENU] Overlay clicked - closing menu');
      setIsMenuOpen(false);
    }} onPointerDown={e => {
      console.log('[MOBILE MENU] Overlay pointer down');
    }} aria-hidden="true" data-id="element-2758"></div>}
    </nav>;
};